<template>
  <div class="deal-item card mb-10">
    <div
      :class="`deal-top ${dealStatus}`"
      v-if="dealStatus === 'archived' || dealStatus === 'selected'"
    >
      <span v-if="dealStatus === 'selected'"
        >The Virtual Deal Room team will be in touch about this transaction.
      </span>
      <span v-if="dealStatus === 'archived'">This deal is archived</span>
      <button
        v-if="dealStatus === 'selected'"
        @click="handleStateChange"
        data-toggle="tooltip"
        title="Archive"
      >
        <inline-svg
          src="media/icons/custom/archive.svg
      "
        />
      </button>
      <button
        v-if="dealStatus === 'archived'"
        @click="handleStateChange"
        data-bs-toggle="tooltip"
        title="Unarchive"
      >
        <inline-svg
          src="media/icons/custom/unarchive.svg
      "
        />
      </button>
    </div>
    <div class="card-header">
      <div class="header-left" @click="handleClick">
        <h4 class="card-title">{{ deal.displayName || deal.title }}</h4>
      </div>
      <div class="header-right">
        <button
          v-if="dealStatus === 'available' && !isExample"
          class="header-right_select"
          @click="handleStateChange"
        >
          Select
        </button>
      </div>
    </div>
    <div class="card-body" @click="handleClick">
      <div class="d-flex flex-column">
        <div class="d-flex flex-wrap">
          <div v-if="deal.industrySectors || deal.sector" class="data-item">
            <span>{{ deal.industrySectors || deal.sector }}</span>
            <label>Sector</label>
          </div>
          <div class="data-item">
            <span>{{ normalizedFinsought || "TBD" }}</span>
            <label>Project cost</label>
          </div>
          <div v-if="humanizedDebtType" class="data-item">
            <span>{{ humanizedDebtType }}</span>
            <label>Financing method</label>
          </div>
          <div v-if="deal.targetRegion" class="data-item">
            <span>{{ deal.targetRegion }}</span>
            <label>Project area</label>
          </div>
        </div>

        <div class="deal-summary">
          {{ isExample ? deal.summary : summaryFormatted }}
          <a class="link-color" v-if="isTextTooLarge && !isExample">See more</a>
        </div>
      </div>
    </div>
    <footer class="card-footer" v-if="deal?.materials?.length">
      <div class="card-footer_counter">
        <inline-svg
          class="card-footer_counter_icon text-muted"
          src="media/icons/duotune/communication/com008.svg"
        /><span class="card-footer_counter_number">{{
          deal.materials.length
        }}</span>
      </div>
      <div class="card-footer_list">
        <span
          >Materials available after signing NDA: {{ listOfMaterials }}</span
        >
      </div>
    </footer>
    <footer v-if="isExample" class="example-badge">
      <router-link :to="{ name: 'deals' }"
        >See all live available deals</router-link
      >
    </footer>
  </div>
</template>

<script>
import _ from "lodash";
import options from "@/core/data/options";
import {
  getMaterialLabel,
  getAmount,
  getCurrency,
} from "@/core/helpers/format";
import conversion from "@/core/helpers/conversion";

const maxNumberOfSummaryCharacters = 270;

export default {
  props: {
    deal: {
      type: Object,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      default: () => {},
    },
    isExample: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    dealStatus() {
      return this.$store.getters["deals/getDealStatus"](this.deal._id);
    },
    interest() {
      return this.$store.getters["account/interest"];
    },
    isTextTooLarge() {
      return this.deal.summary?.length > maxNumberOfSummaryCharacters;
    },
    summaryFormatted() {
      if (this.isTextTooLarge) {
        return (
          this.deal.summary?.slice(0, maxNumberOfSummaryCharacters) + "..."
        );
      }
      return this.deal.summary;
    },
    humanizedDebtType() {
      const debt = _.get(this.deal, "debtType.length");
      if (debt) {
        const parts = this.deal.debtType.map((item) =>
          conversion.humanizeDebtType(item)
        );
        return parts.join(", ");
      }
      return "";
    },
    normalizedFinsought() {
      if (this.deal._latestUSDfinsoughtCurrency) {
        return (
          conversion.formatToShortCurrency(
            this.deal._latestUSDfinsoughtCurrency
          ) + " (est.)"
        );
      }
      if (this.deal.finsoughtCurrency) {
        const currency = getCurrency(this.deal.finsoughtCurrency);
        const amount = getAmount(this.deal.finsoughtCurrency);
        if (amount) {
          return conversion.formatToShortCurrency(amount, currency) + " (est.)";
        }
      }
      if (this.deal.finsought) {
        return (
          conversion.formatToShortCurrency(this.deal.finsought) + " (est.)"
        );
      }
      return "";
    },
    listOfMaterials() {
      let materialsString = "";
      this.deal.materials.forEach((material, index) => {
        const materialLabel = getMaterialLabel(material).label;
        if (index === this.deal?.materials?.length - 1) {
          materialsString += materialLabel + ".";
        } else {
          materialsString += materialLabel + "; ";
        }
      });
      return materialsString;
    },
  },
  methods: {
    format(dealRanges) {
      return (
        (options.dealRanges.find((d) => d.value === dealRanges) || {}).name ||
        dealRanges
      );
    },
    handleClick() {
      this.$emit("select", this.deal);
    },
    handleStateChange() {
      const stateChangeMap = {
        available: this.handleSelect,
        selected: this.handleArchive,
        archived: this.handleUnarchive,
      };
      return stateChangeMap[this.dealStatus]();
    },
    handleSelect() {
      const payload = { ...this.interest };
      payload.deals = _.uniqBy((payload.deals || []).concat(this.deal), "_id");
      return this.$store.dispatch("account/update", payload);
    },
    handleUnarchive() {
      const payload = { ...this.interest };
      const deal = payload.deals.find((d) => d._id === this.deal._id);
      deal.archivedAt = null;
      deal.addedAt = Date.now();
      return this.$store.dispatch("account/update", payload);
    },
    handleArchive() {
      const payload = { ...this.interest };
      const deal = payload.deals.find((d) => d._id === this.deal._id);
      deal.archivedAt = Date.now();
      return this.$store.dispatch("account/update", payload);
    },
  },
};
</script>

<style scoped lang="scss">
.deal-item {
  position: relative;
  cursor: pointer;
  box-shadow: 0px 0px 10px rgba(21, 135, 237, 0.31);
  border-radius: 16px;
  border-top: 9px solid rgba(208, 233, 255, 0.53);

  &:hover {
    box-shadow: 6px 6px 21px rgba(76, 87, 125, 0.26);
  }
}

.deal-top {
  width: 100%;
  height: 43px;

  display: flex;
  justify-content: space-between;
  align-items: center;

  padding-left: 20px;
  padding-right: 20px;
}
.selected {
  background-color: #dca93d12;

  // After delay
  animation-delay: 100ms;
  // Change to: "Variant3";
  // Animate: Smart animate;
  animation-timing-function: ease-in;
  animation-duration: 300ms;

  span {
    font-weight: 400;
    font-size: 12px;
    color: #7e8299;
  }

  button {
    border: none;
    background-color: inherit;
  }
}

.archived {
  background-color: #f9f9f9;

  span {
    font-weight: 400;
    font-size: 12px;
    color: #7e8299;
  }

  button {
    border: none;
    background-color: inherit;
  }
}
.header-left {
  flex: 1;
  display: flex;
  h4 {
    max-width: 700px;
    padding-bottom: 15px;
  }
  img {
    margin-top: 24px;
  }
  .card-title {
    align-items: flex-start;
    margin-top: 22px;
    font-size: 18px;
    color: rgba(45, 117, 181, 0.88);
  }
}
.header-right {
  display: flex;
  align-items: center;

  &_select {
    width: 140px;
    height: 34px;

    background: #ffffff;
    border: 1.2px solid #2d75b5;
    border-radius: 6px;

    font-weight: 500;
    font-size: 15px;

    color: #2d75b5;
  }
}
.flag-icon {
  width: 25px;
  height: 17px;
}
.deal-badge {
  color: #c8355d;
  font-size: 14px;
  font-weight: 500;
}
.data-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  padding-right: 20px;
  padding-bottom: 10px;

  label {
    color: #7e8299;
    font-size: 13px;
    font-size: 400;
    margin-right: 14px;
  }
  span {
    color: #2d75b5;
    font-size: 14px;
  }
}
.deal-summary {
  margin-top: 10px;
  color: #2d75b5;
  font-size: 16px;
  line-height: 24px;
}

.card-footer {
  width: 100%;

  display: flex;
  justify-content: flex-start;
  align-items: center;

  column-gap: 10px;

  border-top: 1px solid #e7edf7;

  padding: 16px 30px;

  &_counter {
    width: 50px;
    height: 32px;

    display: flex;
    justify-content: center;
    align-items: center;

    background: #ffffff;
    border: 1px dashed #e4e6ef;
    border-radius: 6px;
    &_number {
      font-weight: 700;
      font-size: 13px;
      color: #80808f;
    }

    &_icon {
      height: 16px;
    }
  }

  &_list {
    font-weight: 400;
    font-size: 13px;
    color: #7e8299;
  }
}
.example-badge {
  width: 100%;
  background: #fafafa;
  font-size: 18px;
  padding: 20px 50px;
  border-radius: 5px;

  display: flex;
  justify-content: center;
  align-items: center;

  a {
    color: #0083fd;
  }
}

.card-header {
  border-bottom: none;
}
</style>
